import { useId, useLayoutEffect, useRef, useState } from "react";
import { useTimelineCtx } from "../ctx";
import {
  HALO_RADIUS,
  PEAK_Y,
  TROUGH_Y,
  WAVE_HEIGHT,
} from "./timeline-horizontal-track-segment";

// One sinusoid per row-block. `startAtPeak` flips parity so row-blocks
// starting at an odd global index begin at the trough. Mask cuts at each
// bullet make the wave visually break under markers.

const SEG_W = 360;

const stroke = {
  stroke: "var(--timeline-color)",
  strokeWidth: 4,
  strokeLinecap: "round" as const,
  fill: "none",
};

// Peak/trough for bullet `i`. Alternation is continuous (global parity)
// so the carousel reads as one uninterrupted zig-zag; `startAtPeak`
// flips the parity for NewLine row-blocks that begin at an odd global
// index. Marker / content placement must use the same global parity
// (see timeline-horizontal-track-segment.tsx) or the dots drift off the
// curve.
const bulletYFor = (i: number, startAtPeak: boolean): number => {
  const isEvenLocal = i % 2 === 0;
  return startAtPeak
    ? isEvenLocal
      ? PEAK_Y
      : TROUGH_Y
    : isEvenLocal
      ? TROUGH_Y
      : PEAK_Y;
};

const buildPath = (itemCount: number, startAtPeak: boolean) => {
  let d = "";
  for (let i = 0; i < itemCount - 1; i++) {
    const startY = bulletYFor(i, startAtPeak);
    const endY = bulletYFor(i + 1, startAtPeak);
    const segStart = i * SEG_W;
    const c1x = segStart + SEG_W / 3;
    const c2x = segStart + (SEG_W * 2) / 3;
    const ex = segStart + SEG_W;
    if (i === 0) d += `M ${segStart} ${startY} `;
    d += `C ${c1x} ${startY}, ${c2x} ${endY}, ${ex} ${endY} `;
  }

  // Tail: one more segment past the last bullet so the curve doesn't
  // visually clip at the last item.
  const lastBulletX = SEG_W * (itemCount - 1);
  const lastBulletY = bulletYFor(itemCount - 1, startAtPeak);
  const tailEndY = lastBulletY === PEAK_Y ? TROUGH_Y : PEAK_Y;
  const tailEndX = lastBulletX + SEG_W;
  // Single-item rows skip the segment loop above, so the path needs
  // its own M command before the tail curve.
  if (itemCount < 2) d = `M ${lastBulletX} ${lastBulletY} `;
  d += `C ${lastBulletX + SEG_W / 3} ${lastBulletY}, ${lastBulletX + (SEG_W * 2) / 3} ${tailEndY}, ${tailEndX} ${tailEndY} `;

  return { d: d.trim(), totalW: tailEndX };
};

export const TimelinePathSinusoid = ({
  itemCount,
  startAtPeak = true,
  firstItemIdx = 0,
}: {
  itemCount: number;
  startAtPeak?: boolean;
  // Global index of this row-block's first item — used to look up
  // per-item marker measurements from the timeline ctx.
  firstItemIdx?: number;
}) => {
  const maskId = `timeline-path-cuts-${useId()}`;
  const svgRef = useRef<SVGSVGElement | null>(null);
  const { markerSizes } = useTimelineCtx();
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const update = () => {
      setSvgSize({ width: el.clientWidth, height: el.clientHeight });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (itemCount < 1) return null;

  const bulletY = (i: number) => bulletYFor(i, startAtPeak);

  const firstBulletLeft = `${HALO_RADIUS}px`;
  const mainWidth = `calc(100% + var(--timeline-items-gap))`;

  const { d, totalW } = buildPath(itemCount, startAtPeak);

  // Convert measured pixel sizes into viewBox units. preserveAspectRatio
  // is "none" so the X / Y scale is non-uniform — ellipses compensate.
  const scaleX = svgSize.width > 0 ? totalW / svgSize.width : 1;
  const scaleY = svgSize.height > 0 ? WAVE_HEIGHT / svgSize.height : 1;
  const defaultR = HALO_RADIUS;
  const CUT_PADDING_PX = 10;

  return (
    <svg
      ref={svgRef}
      className="timeline__path tw-pointer-events-none tw-absolute tw-block"
      preserveAspectRatio="none"
      viewBox={`0 0 ${totalW} ${WAVE_HEIGHT}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{
        left: firstBulletLeft,
        top: 0,
        width: mainWidth,
        height: "100%",
        zIndex: 0,
      }}
    >
      <defs>
        {/* Explicit x/y/w/h + maskUnits required — without them the
            default objectBoundingBox rect doesn't cover the full sinusoid
            and the path renders unmasked (no gaps at bullets). */}
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          maskContentUnits="userSpaceOnUse"
          x="0"
          y="0"
          width={totalW}
          height={WAVE_HEIGHT}
        >
          <rect x="0" y="0" width={totalW} height={WAVE_HEIGHT} fill="white" />
          {Array.from({ length: itemCount }).map((_, i) => {
            const size = markerSizes.get(firstItemIdx + i);
            const rx = size
              ? (size.width / 2 + CUT_PADDING_PX) * scaleX
              : defaultR;
            const ry = size
              ? (size.height / 2 + CUT_PADDING_PX) * scaleY
              : defaultR;
            return (
              <ellipse
                key={i}
                cx={i * SEG_W}
                cy={bulletY(i)}
                rx={rx}
                ry={ry}
                fill="black"
              />
            );
          })}
        </mask>
      </defs>
      <path d={d} mask={`url(#${maskId})`} {...stroke} />
    </svg>
  );
};
